/**
 * AuthProvider — manages JWT lifecycle and admin-level role enforcement.
 * Only allows super_admin, org_admin, and compliance_officer roles.
 * Wraps the application to provide authentication context.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { buildMsalConfig, entraLoginRequest, getMsalInstance } from "@/lib/msal-config";
import {
  API_BASE_PATH,
  clearTokens,
  csrfFromAccessToken,
  getAccessToken,
  getCsrfToken,
  setTokens,
  trySilentRefresh,
} from "@/api/client";
import { collectDeviceSignals } from "@/lib/device-signals";

/* ─── Types ─────────────────────────────────────────────────── */

export type AdminRole = "super_admin" | "org_admin" | "compliance_officer";

const ADMIN_ROLES: AdminRole[] = [
  "super_admin",
  "org_admin",
  "compliance_officer",
];

export interface AdminUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  organization_id?: string;
  organization_name?: string;
  avatar_url?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthContextValue {
  /** Currently authenticated admin user, or null. */
  user: AdminUser | null;
  /** Whether the user is authenticated with an admin role. */
  isAuthenticated: boolean;
  /** Whether auth state is still loading on mount. */
  isLoading: boolean;
  /** Log in with credentials. Returns mfa_required flag. */
  login: (req: LoginRequest) => Promise<{ mfaRequired: boolean }>;
  /** Log the current user out. */
  logout: () => Promise<void>;
  /** Force-refresh the current user profile from the server. */
  refreshAuth: () => Promise<AdminUser | null>;
  /** Check if the user has a specific admin role. */
  hasRole: (role: AdminRole) => boolean;
  /** Assert that the user is admin-level; throws if not. */
  requireAdmin: () => void;
  /** Verify an MFA code after login returns mfaRequired=true. */
  verifyMfa: (code: string) => Promise<void>;
  /** Redirect to Microsoft Entra sign-in. */
  loginWithMicrosoft: () => Promise<void>;
  /** Finish Entra redirect — exchange id_token with backend. */
  completeEntraLogin: (idToken: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/** Idle session timeout for elevated-privilege admin sessions — 10 minutes */
const IDLE_TIMEOUT_MS = 10 * 60 * 1000;

function authHeaders(mutating = false): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (mutating) {
    const csrf = getCsrfToken();
    if (csrf) {
      headers["X-CSRF-Token"] = csrf;
    }
  }
  return headers;
}

function isAdminRole(role: string): role is AdminRole {
  return (ADMIN_ROLES as string[]).includes(role);
}

/* ─── Provider ──────────────────────────────────────────────── */

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // mfa_token issued by /auth/login when MFA is required
  const [pendingMfaToken, setPendingMfaToken] = useState<string | null>(null);
  // idle timeout timer
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isAuthenticated = !!user && isAdminRole(user.role);

  /** Reset the idle timeout on any user activity. */
  const resetIdle = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (!user) return;
    idleTimer.current = setTimeout(() => {
      clearTokens();
      setUser(null);
      window.location.href = "/login?reason=session_timeout";
    }, IDLE_TIMEOUT_MS);
  }, [user]);

  /** Fetch the current user profile from the backend. */
  const refreshAuth = useCallback(async (): Promise<AdminUser | null> => {
    try {
      const token = getAccessToken();
      if (!token) {
        setUser(null);
        return null;
      }

      const res = await fetch(`${API_BASE_PATH}/auth/profile`, {
        credentials: "include",
        headers: authHeaders(),
      });

      if (!res.ok) {
        clearTokens();
        setUser(null);
        return null;
      }

      const data = await res.json();
      const profile: AdminUser = data.data ?? data;

      // Block non-admin roles
      if (!isAdminRole(profile.role)) {
        clearTokens();
        setUser(null);
        return null;
      }

      setUser(profile);
      return profile;
    } catch {
      clearTokens();
      setUser(null);
      return null;
    }
  }, []);

  /** Log in and store tokens. Only allows admin-level roles. */
  const login = useCallback(
    async (req: LoginRequest): Promise<{ mfaRequired: boolean }> => {
      // Backend expects { login, password } — login can be email OR username
      const res = await fetch(`${API_BASE_PATH}/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders(true),
        body: JSON.stringify({
          login: req.email,
          password: req.password,
          device: collectDeviceSignals(),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error?.message ?? err.error ?? "Login failed");
      }

      const data = await res.json();
      const payload = data.data ?? data;

      if (payload.mfa_required) {
        // Store the short-lived mfa_token so verifyMfa can send it
        setPendingMfaToken(payload.mfa_token ?? null);
        return { mfaRequired: true };
      }

      const userRole = payload.user?.role;
      if (!isAdminRole(userRole)) {
        throw new Error(
          "Access denied. Only administrators can access this portal.",
        );
      }

      setTokens(
        payload.access_token,
        payload.csrf_token ?? csrfFromAccessToken(payload.access_token),
        payload.refresh_token,
      );
      setUser(payload.user);
      resetIdle();
      return { mfaRequired: false };
    },
    [resetIdle],
  );

  /** Verify MFA code — called after login returns mfa_required=true. */
  const verifyMfa = useCallback(async (code: string): Promise<void> => {
    if (!pendingMfaToken) {
      throw new Error("No pending MFA session. Please log in again.");
    }

    const res = await fetch(`${API_BASE_PATH}/auth/mfa/verify`, {
      method: "POST",
      credentials: "include",
      headers: authHeaders(true),
      body: JSON.stringify({
        mfa_token: pendingMfaToken,
        code,
        device: collectDeviceSignals(),
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error?.message ?? err.error ?? "MFA verification failed");
    }

    const data = await res.json();
    const payload = data.data ?? data;

    const userRole = payload.user?.role;
    if (!isAdminRole(userRole)) {
      throw new Error(
        "Access denied. Only administrators can access this portal.",
      );
    }

    setTokens(
      payload.access_token,
      payload.csrf_token ?? csrfFromAccessToken(payload.access_token),
      payload.refresh_token,
    );
    setUser(payload.user);
    setPendingMfaToken(null);
    resetIdle();
  }, [pendingMfaToken, resetIdle]);

  /** Fetch public SSO config for admin portal. */
  const fetchSSOConfig = useCallback(async () => {
    const res = await fetch(`${API_BASE_PATH}/auth/sso/config?portal=admin`);
    if (!res.ok) throw new Error("Could not load SSO configuration");
    const body = await res.json();
    return body.data ?? body;
  }, []);

  /** Start Microsoft Entra redirect login. */
  const loginWithMicrosoft = useCallback(async () => {
    const sso = await fetchSSOConfig();
    if (!sso.entra_enabled) {
      throw new Error("Microsoft sign-in is not enabled");
    }
    const cfg = buildMsalConfig(sso.tenant_id, sso.client_id);
    const msal = getMsalInstance(cfg ?? undefined);
    if (!msal) {
      throw new Error("Microsoft sign-in is not configured");
    }
    await msal.initialize();
    await msal.loginRedirect(entraLoginRequest);
  }, [fetchSSOConfig]);

  /** Complete Entra login after redirect callback. */
  const completeEntraLogin = useCallback(
    async (idToken: string): Promise<void> => {
      const res = await fetch(`${API_BASE_PATH}/auth/sso/microsoft`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders(true),
        body: JSON.stringify({
          id_token: idToken,
          device: collectDeviceSignals(),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error?.message ?? err.error ?? "Microsoft sign-in failed");
      }

      const data = await res.json();
      const payload = data.data ?? data;

      if (payload.mfa_required) {
        throw new Error("Unexpected MFA step after Microsoft sign-in");
      }

      const userRole = payload.user?.role;
      if (!isAdminRole(userRole)) {
        throw new Error("Access denied. Only administrators can access this portal.");
      }

      setTokens(
        payload.access_token,
        payload.csrf_token ?? csrfFromAccessToken(payload.access_token),
        payload.refresh_token,
      );
      setUser(payload.user);
      resetIdle();
    },
    [resetIdle],
  );

  /** Log out, clear tokens, and reset state. */
  const logout = useCallback(async () => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    try {
      const token = getAccessToken();
      if (token) {
        await fetch(`${API_BASE_PATH}/auth/logout`, {
          method: "POST",
          credentials: "include",
          headers: authHeaders(true),
        }).catch(() => {});
      }
    } catch {
      // Best-effort
    }
    clearTokens();
    setUser(null);
    setPendingMfaToken(null);
  }, []);

  /** Check if the user has a specific admin role. */
  const hasRole = useCallback(
    (role: AdminRole): boolean => {
      return user?.role === role;
    },
    [user],
  );

  /** Assert that the user is admin-level; throws if not. */
  const requireAdmin = useCallback(() => {
    if (!user || !isAdminRole(user.role)) {
      throw new Error("Admin access required");
    }
  }, [user]);

  // On mount: restore session from HttpOnly refresh cookie when possible
  useEffect(() => {
    void (async () => {
      if (!getAccessToken()) {
        await trySilentRefresh();
      }
      if (getAccessToken()) {
        await refreshAuth();
      }
      setIsLoading(false);
    })();
  }, [refreshAuth]);

  // Idle session timeout — reset timer on any user interaction
  useEffect(() => {
    if (!isAuthenticated) return;
    const events = ["mousemove", "keydown", "mousedown", "touchstart", "scroll"];
    const handler = () => resetIdle();
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetIdle(); // start the timer when authenticated
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [isAuthenticated, resetIdle]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      logout,
      refreshAuth,
      hasRole,
      requireAdmin,
      verifyMfa,
      loginWithMicrosoft,
      completeEntraLogin,
    }),
    [user, isAuthenticated, isLoading, login, logout, refreshAuth, hasRole, requireAdmin, verifyMfa, loginWithMicrosoft, completeEntraLogin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ─── Hook ──────────────────────────────────────────────────── */

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}
